// apps/web/src/pages/admin/reservations/useExportReservations.ts —
// Phase 6 plan 06-04 (EXP-01..03).
//
// React Query mutation hook for `Mutation.exportReservations`. The
// mutation returns a signed download URL (`/api/export/{token}.{ext}`)
// that the admin's browser opens in a new tab. The token is HMAC-SHA256
// signed with JWT_SECRET + has a 5-minute TTL + is one-time use (the
// server-side Redis key is deleted on first GET).
//
// `window.open(url, '_blank', 'noopener,noreferrer')` follows the
// project convention for "open in a new tab without reverse-tabnabbing"
// — the `noopener` flag prevents the new tab from accessing
// `window.opener` (defense in depth against tabnabbing attacks).
//
// Errors surface as a thrown Error; the ExportButton catches them and
// surfaces the message via the existing react-hot-toast toast.

import { useMutation } from "@tanstack/react-query";
import { gqlRequest } from "../../../lib/api";

const EXPORT_MUTATION = `
  mutation ExportReservations($format: ExportFormat!, $from: String!, $to: String!) {
    exportReservations(format: $format, from: $from, to: $to) {
      url
      expiresAt
      rowCount
      format
      locale
    }
  }
`;

export type ExportFormatInput = "CSV" | "PDF";

export interface ExportReservationsArgs {
  format: ExportFormatInput;
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
}

export interface ExportReservationsResult {
  url: string;
  expiresAt: string;
  rowCount: number;
  format: ExportFormatInput;
  locale: string;
}

export function useExportReservations() {
  return useMutation<ExportReservationsResult, Error, ExportReservationsArgs>({
    mutationFn: async (args) => {
      const data = await gqlRequest<{
        exportReservations: ExportReservationsResult;
      }>(EXPORT_MUTATION, {
        format: args.format,
        from: args.from,
        to: args.to,
      });
      return data.exportReservations;
    },
    onSuccess: (data) => {
      if (typeof window !== "undefined") {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    },
  });
}
